import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Book, ChevronRight, Loader2, Star, Eye, User } from "lucide-react";
import { Card, CardContent, CardFooter } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { toast } from "sonner";

interface Author {
  id: string;
  username: string | null;
  avatar_url: string | null;
}

interface WebNovel {
  id: string;
  title: string;
  cover_url: string;
  description: string | null;
  created_at: string | null;
  author_id: string | null;
  updated_at: string | null;
  category: string | null;
  age_rating: string | null;
  rating?: number;
  views?: number;
  author?: Author;
}

export const WebNovelsGrid = () => {
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Fetch webnovels from Supabase
  const { data: webnovels, isLoading, error } = useQuery<WebNovel[]>({
    queryKey: ["webnovels"],
    queryFn: async () => {
      try {
        // First get all webnovels
        const { data: webnovelsData, error: webnovelsError } = await supabase
          .from("webnovels")
          .select(`
            id,
            title,
            cover_url,
            description,
            created_at,
            author_id,
            updated_at,
            category,
            age_rating
          `)
          .order("created_at", { ascending: false });

        if (webnovelsError) {
          console.error("Error fetching webnovels:", webnovelsError);
          throw webnovelsError;
        }

        if (!webnovelsData || webnovelsData.length === 0) {
          return [] as WebNovel[];
        }

        // Get all author IDs to fetch profiles data
        const authorIds = webnovelsData
          .map(novel => novel.author_id)
          .filter(Boolean) as string[];
        
        // Fetch author profiles
        const { data: authorsData, error: authorsError } = await supabase
          .from("profiles")
          .select(`
            id,
            username,
            avatar_url
          `)
          .in("id", authorIds);

        if (authorsError) {
          console.error("Error fetching authors:", authorsError);
          throw authorsError;
        }

        // Map authors to webnovels
        return webnovelsData.map(novel => {
          const author = authorsData?.find(a => a.id === novel.author_id) || {
            id: novel.author_id || "unknown",
            username: "Autor Desconhecido",
            avatar_url: "https://github.com/shadcn.png"
          };

          return {
            ...novel,
            author,
            rating: Math.floor(Math.random() * 5) + 1, // Random rating between 1-5
            views: Math.floor(Math.random() * 1000) + 100 // Random views
          };
        });
      } catch (err) {
        console.error("Error in queryFn:", err);
        toast.error("Erro ao carregar webnovels");
        return [];
      }
    },
  });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div 
              key={i} 
              variants={itemVariants}
              className="h-96"
            >
              <Card className="w-full h-full overflow-hidden">
                <Skeleton className="h-[70%] w-full" />
                <div className="p-4">
                  <Skeleton className="h-4 w-3/4 my-2" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <motion.div 
        className="container mx-auto py-8 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-xl font-bold text-red-500 mb-2">Erro</h2>
        <p className="text-muted-foreground">Não foi possível carregar as webnovels. Por favor, tente novamente mais tarde.</p>
        <Button 
          className="mt-4" 
          onClick={() => window.location.reload()}
          variant="outline"
        >
          Tentar novamente
        </Button>
      </motion.div>
    );
  }

  if (!webnovels || webnovels.length === 0) {
    return (
      <motion.div 
        className="container mx-auto py-8 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-md mx-auto">
          <Book className="mx-auto h-12 w-12 text-muted-foreground opacity-50 mb-4" />
          <h2 className="text-xl font-medium mb-2">Nenhuma WebNovel encontrada</h2>
          <p className="text-muted-foreground mb-6">
            Não encontramos nenhuma WebNovel em nosso sistema.
          </p>
          <Button onClick={() => navigate("/create")}>
            <Book className="mr-2 h-4 w-4" />
            Criar uma WebNovel
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="container mx-auto py-4 relative">
      <motion.div 
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        {webnovels.map((webnovel) => (
          <motion.div 
            key={webnovel.id}
            variants={itemVariants}
            className="h-full"
            whileHover={{ 
              scale: 1.03, 
              boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.15)",
              transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.98 }}
            onMouseEnter={() => setHoveredId(webnovel.id)}
            onMouseLeave={() => setHoveredId(null)}
            onClick={() => navigate(`/webnovel/${webnovel.id}/chapter/1`)}
          >
            <Card className="w-full h-full overflow-hidden cursor-pointer group">
              <div className="relative aspect-[9/16] overflow-hidden">
                <img 
                  src={webnovel.cover_url} 
                  alt={webnovel.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                  <AnimatePresence>
                    {hoveredId === webnovel.id && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="text-white"
                      >
                        <p className="text-sm line-clamp-3">{webnovel.description}</p>
                        <div className="mt-2 flex items-center">
                          <Button size="sm" variant="outline" className="text-white border-white/30 bg-white/10 backdrop-blur-sm">
                            Ler agora
                            <ChevronRight className="ml-1 h-4 w-4" />
                          </Button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              <CardFooter className="p-4 flex flex-col items-start">
                <h3 className="font-medium line-clamp-1">{webnovel.title}</h3>
                
                <div className="flex items-center gap-1 mt-1">
                  <User className="h-3 w-3 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">{webnovel.author?.username || "Autor Desconhecido"}</p>
                </div>
                
                {webnovel.category && (
                  <Badge variant="outline" className="mt-2 text-xs">
                    {webnovel.category}
                  </Badge>
                )}
                
                <div className="flex w-full justify-between items-center mt-2">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i}
                        size={12}
                        className={i < (webnovel.rating || 0) ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}
                      />
                    ))}
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Eye className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{webnovel.views}</span>
                  </div>
                </div>
              </CardFooter>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};
